import _ from 'lodash';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import React, { FC } from 'react';

import { LoadingButton } from '@mui/lab';
import {
  Collapse,
  Container,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';

import { pointAPI } from '../services/PointService';
import { onbordingAPI } from '../services/onbordingService';

const StyledHeader = styled('span')(({ theme }) => ({
  fontSize: '14px',
}));

const StyledHeaderFirst = styled('span')(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 'bold',
}));

const StyledRoot = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

const StyledContent = styled('div')(({ theme }) => ({
  maxWidth: 480,
  margin: 'auto',
  minHeight: '100vh',
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
}));

const NewPoint: FC = () => {
  const { t } = useTranslation();

  const {
    data: onbordingData,
    isLoading,
    refetch,
  } = onbordingAPI.useGetStatusQuery(null);

  const [completePointSetup] = onbordingAPI.useCompletePointSetupMutation();

  const hasPoint = onbordingData?.data?.hasPoint;

  const demoDataCreated = onbordingData?.data?.demoDataCreated;

  const [createPoint, { isError, error: errorCreatePoint }] =
    pointAPI.useCreatePointMutation();

  const navigate = useNavigate();

  if (hasPoint) {
    navigate(`/`);
  }

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<{
    name: string;
    phone_number: string;
  }>();

  const onSubmit: SubmitHandler<any> = async (args) => {
    await createPoint(args);

    if (!demoDataCreated) {
      await completePointSetup(null);
    }

    await refetch();
  };

  return (
    <>
      {!isLoading && !hasPoint && (
        <HelmetProvider>
          <Helmet>
            <title>{t(`New Point`)}</title>
          </Helmet>

          <StyledRoot>
            <Container maxWidth="sm" data-testid="first-point-page">
              <StyledContent>
                <Typography variant="h5" gutterBottom sx={{ mb: 5 }}>
                  {t('create_first')}{' '}
                  <StyledHeaderFirst>{t('point.point')} </StyledHeaderFirst>
                </Typography>

                <form onSubmit={handleSubmit(onSubmit)}>
                  <Collapse in={isError} timeout={200}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ my: 2 }}
                    >
                      <Typography color="error">
                        {errorCreatePoint && 'data' in errorCreatePoint && (
                          <>{errorCreatePoint?.data}</>
                        )}
                      </Typography>
                    </Stack>
                  </Collapse>

                  <Stack spacing={3} marginBottom={2}>
                    <Controller
                      control={control}
                      name="name"
                      rules={{
                        required: `${t('form.required')}`,
                      }}
                      render={({ field }) => {
                        return (
                          <TextField
                            label={t(`point.name`)}
                            sx={{ mb: 5 }}
                            {...field}
                            error={!!errors.name}
                            helperText={errors.name?.message}
                          />
                        );
                      }}
                    />
                  </Stack>

                  <Stack spacing={3} marginBottom={3}>
                    <Controller
                      control={control}
                      name="phone_number"
                      rules={{
                        required: `${t('form.required')}`,
                      }}
                      render={({ field }) => {
                        return (
                          <TextField
                            label={t(`point.phone_number`)}
                            sx={{ mb: 5 }}
                            {...field}
                            error={!!errors.phone_number}
                            helperText={errors.phone_number?.message}
                          />
                        );
                      }}
                    />
                  </Stack>

                  <LoadingButton
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                  >
                    {t(`Create`)}
                  </LoadingButton>
                </form>
              </StyledContent>
            </Container>
          </StyledRoot>
        </HelmetProvider>
      )}
    </>
  );
};

export default NewPoint;
